import { environmentHttpClient } from "client/Api/httpClient";
import { documentsConfiguration, deviceHelper } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	DocumentItemViewModel,
	DocumentItemType,
	FileViewModel,
	FolderViewModel,
	ListLocationModel,
	OneNoteNotebookViewModel
} from "client/Api/Documents/SharePoint/common";
import { NewDocumentType } from "client/Api/Documents/common";

const controllerRelativeUrl: string = "Documents/SharePoint/Action";

export interface DocumentCreationModel {
	name: string;
	folderId: number | null;
	location: ListLocationModel;
	documentType: NewDocumentType;
}

export interface DocumentCreationResult {
	newItem: FileViewModel | OneNoteNotebookViewModel;
	suffix: number;
}

export type RenameModel = {
	location: ListLocationModel;
	documentId: number;
	newDocumentName: string;
};

export function createFile(
	data: DocumentCreationModel,
	signal?: AbortSignal
): Promise<DocumentCreationResult> {
	return environmentHttpClient.post<DocumentCreationResult>(
		controllerRelativeUrl + "/CreateFile",
		data,
		{
			async: deviceHelper.wopi.areAsyncRequestsAllowed(documentsConfiguration().sharePoint.wopi.openTarget),
			signal
		}
	);
}

export function createFileFromTemplate(
	model: DocumentTemplateCreationModel,
	signal?: AbortSignal
): Promise<DocumentCreationResult> {
	return environmentHttpClient.post<DocumentCreationResult>(
		controllerRelativeUrl + "/CreateFileFromTemplate",
		model,
		{
			signal,
			async: deviceHelper.wopi.areAsyncRequestsAllowed(documentsConfiguration().sharePoint.wopi.openTarget)
		}
	);
}

export interface DocumentTemplateCreationModel {
	name: string;
	folderId: number | null;
	location: ListLocationModel;
	templateUrl: string;
}

export interface FolderCreationModel {
	name: string;
	folderId: number | null;
	location: ListLocationModel;
}

export function createFolder(
	data: FolderCreationModel,
	signal?: AbortSignal
): Promise<FolderViewModel> {
	return environmentHttpClient.post<FolderViewModel>(
		controllerRelativeUrl + "/CreateFolder",
		data,
		{ signal }
	);
}

export type FailedToDeleteDocumentModel = {
	itemName: string;
	errorMessage: string;
};

export type DeleteDocumentsResponse = {
	failedItems: FailedToDeleteDocumentModel[];
	deletedItems: number[];
};

export function deleteDocuments(
	location: ListLocationModel,
	itemIds: number[]
): Promise<DeleteDocumentsResponse> {
	return environmentHttpClient.post<DeleteDocumentsResponse>(
		controllerRelativeUrl + "/DeleteDocuments",
		{
			location,
			itemIds
		}
	);
}

export interface DocumentMovingModel {
	id: number;
	type: DocumentItemType;
}

export interface MoveDocumentsModel {
	location: ListLocationModel;
	newParentFolderId: number | null;
	documents: DocumentMovingModel[];
}

export function moveItems(data: MoveDocumentsModel, signal?: AbortSignal): Promise<void> {
	return environmentHttpClient.post<void>(
		controllerRelativeUrl + "/MoveDocuments",
		data,
		{ signal }
	);
}

export function renameFile(
	data: RenameModel,
	signal?: AbortSignal
): Promise<DocumentItemViewModel> {
	return environmentHttpClient.post<DocumentItemViewModel>(
		controllerRelativeUrl + "/RenameFile",
		data,
		{ signal }
	);
}

export function renameFolder(
	data: RenameModel,
	signal?: AbortSignal
): Promise<DocumentItemViewModel> {
	return environmentHttpClient.post<DocumentItemViewModel>(
		controllerRelativeUrl + "/RenameFolder",
		data,
		{ signal }
	);
}

export type CopyModel = {
	newFileName: string | null;
	sourceLocation: ListLocationModel;
	sourceFileId: number;
	destinationLocation: ListLocationModel;
	destinationFolderId: number | null;
};

export function copyFile(
	data: CopyModel,
	signal?: AbortSignal
): Promise<FileViewModel> {
	return environmentHttpClient.post<FileViewModel>(
		controllerRelativeUrl + "/Copy",
		data,
		{ signal }
	);
}

export function getGrooveUrl(location: ListLocationModel, signal?: AbortSignal): Promise<string> {
	return environmentHttpClient.get<string>(
		controllerRelativeUrl + "/GetGrooveUrl",
		{ location },
		{ signal }
	);
}

export function getOneDriveUrl(
	location: ListLocationModel,
	folderId: number | nil,
	signal?: AbortSignal
): Promise<string> {
	return environmentHttpClient.get<string>(
		controllerRelativeUrl + "/GetOneDriveUrl",
		{
			location,
			folderId
		},
		{ signal }
	);
}
